import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Trial } from "../components/sidebar/types/types";
import { useHasCookieConsent } from "./useHasCookieConsent";

const waitForHotjarToLoad = (resolve) => {
  let checkHotjarLoaded = null;

  // ensures we don't wait indefinitely
  const timeoutHandler = () => {
    if (checkHotjarLoaded) clearInterval(checkHotjarLoaded);
    resolve();
  };
  const timeout = setTimeout(timeoutHandler, 2000);

  // waits for Hotjar to load
  checkHotjarLoaded = setInterval(() => {
    if (typeof window.hj === "function") {
      clearInterval(checkHotjarLoaded);
      clearTimeout(timeout);
      resolve();
    }
  }, 100);
};

const loadHotjarScript = (): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof window.hj === "function") {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[src*="hotjar-"]');
    if (existingScript) {
      waitForHotjarToLoad(resolve);
      return;
    }

    // Define Hotjar settings globally
    window._hjSettings = {
      hjid: 391767,
      hjsv: 6,
    };

    // Initialize the hj function if it doesn't exist
    if (!window.hj) {
      window.hj = function (...args) {
        window.hj.q = window.hj.q || [];
        window.hj.q.push(args);
      };
    }

    // Create and configure the script element
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`;

    document.head.appendChild(script);

    waitForHotjarToLoad(resolve);
  });
};

export const useHotjarTracking = ({
  trial,
  businessUserId,
  fetchedShellData,
}: {
  trial: Trial | null;
  businessUserId: string;
  fetchedShellData: boolean;
}): void => {
  const hasCookieConsent = useHasCookieConsent();
  const [hotjarLoaded, setHotjarLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined" && hasCookieConsent && fetchedShellData && !hotjarLoaded) {
      loadHotjarScript()
        .then(() => {
          setHotjarLoaded(true);
        })
        .catch(() => {
          // silent fail for Hotjar loading errors
        });
    }
  }, [hasCookieConsent, fetchedShellData, hotjarLoaded]);

  useEffect(() => {
    const shouldIdentifyHotjarUser =
      fetchedShellData &&
      hasCookieConsent &&
      hotjarLoaded &&
      (trial?.trialState === "TrialActive" || trial?.trialState === "TrialCancelled") &&
      trial?.isLegacy === false &&
      typeof window !== "undefined" &&
      typeof window.hj === "function";

    if (shouldIdentifyHotjarUser) {
      try {
        const hotjarAttributes = {
          hasTrial: trial?.hasTrial,
          trialDuration: trial?.duration,
          subscriptionStartDate: trial?.subscriptionStartDate,
          trialState: trial?.trialState,
          planName: trial?.planName,
          isLegacy: trial?.isLegacy,
        };

        window.hj("identify", businessUserId, hotjarAttributes);
      } catch (error) {
        // silently handle error but don't crash the app
      }
    }
  }, [trial, businessUserId, fetchedShellData, hasCookieConsent, hotjarLoaded, location.pathname]);
};

export default useHotjarTracking;
